import type { TransitionType } from "./timeline";

export interface TransitionDefinition {
  type: TransitionType;
  label: string;
  icon: string; // emoji for quick visual identification
  description: string;
}

export const TRANSITION_DEFINITIONS: TransitionDefinition[] = [
  { type: "cut", label: "Cut", icon: "✂️", description: "Instant cut (no transition)" },
  { type: "crossfade", label: "Crossfade", icon: "🔄", description: "Smooth blend between clips" },
  { type: "fade-to-black", label: "Fade to Black", icon: "⬛", description: "Fade out to black, then in" },
  { type: "fade-to-white", label: "Fade to White", icon: "⬜", description: "Fade out to white, then in" },
  { type: "slide-left", label: "Slide Left", icon: "⬅️", description: "Next clip slides in from right" },
  { type: "slide-right", label: "Slide Right", icon: "➡️", description: "Next clip slides in from left" },
  { type: "slide-up", label: "Slide Up", icon: "⬆️", description: "Next clip slides in from bottom" },
  { type: "slide-down", label: "Slide Down", icon: "⬇️", description: "Next clip slides in from top" },
  { type: "zoom-in", label: "Zoom In", icon: "🔍", description: "Zoom into transition" },
  { type: "zoom-out", label: "Zoom Out", icon: "🔎", description: "Zoom out transition" },
  { type: "spin", label: "Spin", icon: "🌀", description: "Spinning rotation transition" },
  { type: "glitch", label: "Glitch", icon: "📺", description: "Digital glitch effect" },
  { type: "wipe-left", label: "Wipe Left", icon: "◀️", description: "Wipe reveal from right to left" },
  { type: "wipe-right", label: "Wipe Right", icon: "▶️", description: "Wipe reveal from left to right" },
  { type: "blur", label: "Blur", icon: "💨", description: "Blur transition between clips" },
  // Intro-only types — only surfaced in the TransitionPicker when
  // context === "intro" (chip before the first clip).
  { type: "fade-in", label: "Fade In from Black", icon: "⬛", description: "Fade up from black at the start" },
  { type: "fade-in-white", label: "Fade In from White", icon: "⬜", description: "Fade in from white at the start" },
  // Outro-only types — surfaced when context === "outro".
  { type: "fade-out", label: "Fade Out to Black", icon: "⬛", description: "Fade out to black at the end" },
  { type: "fade-out-white", label: "Fade Out to White", icon: "⬜", description: "Fade out to white at the end" },
];

// Types valid for a between-clips boundary (transitions[i]). Everything
// except the intro-/outro-only flavors.
export const BETWEEN_CLIPS_TRANSITION_TYPES: TransitionType[] = [
  "cut", "crossfade", "fade-to-black", "fade-to-white",
  "slide-left", "slide-right", "slide-up", "slide-down",
  "zoom-in", "zoom-out", "spin", "glitch",
  "wipe-left", "wipe-right", "blur",
];

// Types valid for the introTransition slot. Only the fade flavors plus
// `cut` (which clears the intro).
export const INTRO_TRANSITION_TYPES: TransitionType[] = [
  "cut", "fade-in", "fade-in-white",
];

export const OUTRO_TRANSITION_TYPES: TransitionType[] = [
  "cut", "fade-out", "fade-out-white",
];

// Definitions valid for a between-clips boundary — what a between-clips
// picker should offer. Derived here (next to the type lists) so both
// platforms share one source of picker eligibility.
export const BETWEEN_CLIPS_TRANSITION_DEFINITIONS: TransitionDefinition[] =
  TRANSITION_DEFINITIONS.filter((def) =>
    BETWEEN_CLIPS_TRANSITION_TYPES.includes(def.type),
  );

export function getTransitionDefinition(
  type: TransitionType,
): TransitionDefinition {
  return (
    TRANSITION_DEFINITIONS.find((t) => t.type === type) ??
    TRANSITION_DEFINITIONS[0]
  );
}
